angular.module('stack.common.api.models.ModelsService', [
  'global'
])

/**
 * The ModelsService service manages data models for
 * all endpoints.  These models are populated via the
 * ApiService and can then be consumed by services
 * within the application to be passed on to
 * controllers and directives for a specific view.
 *
 * @class ModelsService
 * @constructor
 * @module stack.common.api.models.ModelsService
 */
.factory('ModelsService', [
  'ApiService',
  'CrudService',
  'ErrorService',
  function (ApiService, CrudService, ErrorService) {
    'use strict';

    var service = {

      /**
       * A collection of models known to the sytem
       *
       * @property models
       * @type {Array<Object>}
       */
      models: [],

      /**
       * Initializes a model from the provided endpoint href
       *
       * @param {string} model - Model to initializes
       * @param {string} href - Endpoint URI
       * @param {Integer} timeout - timeout for xhr
       * @param {string} server - path to remote server
       * @param {string} token - token for remote server
       * @return {Promise<Object>} initial model
       */
      initialize: function initialize(model, href, timeout, server, token) {
        var returnValue;
        if (server) {
          returnValue = ApiService
            .remoteRequest(href, { server: server, token: token })
            .then(function (response) {
              service.write(model, response.data);
              return response;
            })
            .catch(function (response) {
              console.log(model);
              console.log(response);
              throw response;
            });
        } else {
          returnValue = ApiService
            .execute(href, 'GET', null, false, timeout)
            .then(function (response) {
              service.write(model, response);
              return response;
            })
            .catch(function (response) {
              console.log(model);
              console.log(response);
              throw response;
            });
        }

        return returnValue;
      },

      /**
       * clears the model array from the service
       *
       * @param {string} model - name of model
       */
      empty: function (model) {
        service.models[model] = [];
      },

      /**
       * Gets a model from the provided endpoint href
       *
       * @param {string} model - Model to get
       * @param {string} href - Endpoint URI
       * @param {Object} data - Data to search for
       * @param {Integer} timeout - timeout for xhr
       * @return {Promise<Object>} model instance
       */
      get: function get(model, href, data, timeout) {
        var returnData;
        if (data.server) {
          returnData = ApiService
            .remoteRequest(href, {
              server: data.server,
              token: data.token
            })
            .then(function (response) {
              var respData = response.data;
              var add;
              if (!service.models[respData.model]) {
                service.models[respData.model] = [];
              }
              add = !_.some(service.models[respData.model], { id: respData.id });
              if (add) {
                service.models[respData.model].push(respData);
              }
              return respData;
            })
            .catch(function (respData) {
              ErrorService.requestError(respData);
              throw respData;
            });
        } else {
          returnData = ApiService
            .execute(href, 'GET', data, false, timeout)
            .then(function (response) {
              var add;
              if (!service.models[response.model]) {
                service.models[response.model] = [];
              }
              add = !_.some(service.models[response.model], { id: response.id });
              if (add) {
                service.models[response.model].push(response);
              }
              return response;
            })
            .catch(function (response) {
              ErrorService.requestError(response);
              throw response;
            });
        }
        return returnData;
      },

      /**
       * Writes data to a model.
       *
       * @param {string} model - Model identifier
       * @param {Object} data - JSON data
       * @return {Object} JSON data
       */
      write: function write(model, data) {
        service.models[model] = data;

        return service.models[model];
      },

      /**
       * Reads data from a model.
       *
       * @param {string} model - Model identifier
       * @return {Object} JSON data
       */
      read: function read(model) {
        return service.models[model];
      },

      /**
       * Returns an instance from a model based on provided criteria
       *
       * @param {string} model - Model key
       * @param {string} key - Property to use for search criteria
       * @param {string} value - Value of property
       * @return {Object} Instance found
       */
      find: function find(model, key, value) {
        return _.find(service.models[model], function (obj) {
          return obj[key] === value;
        });
      },

      /**
       * Creates a new entry on a model
       *
       * @param {string} model - Model key
       * @param {Object} data - JSON data
       * @return {Promise<Object>} New model instance
       */
      create: function create(model, data) {
        var found = _.find(service.models[model.model], function (obj) {
          return obj[model.primaryKey] === model[model.primaryKey];
        });

        var href = _.find(found.crud, { id: 'post' }).href;

        return ApiService
          .execute(href, 'POST', data)
          .then(function (response) {
            if (service.models[model.label]) {
              service.models[model.label].push(response);
            }
            return response;
          })
          .catch(function (response) {
            ErrorService.requestError(response);
            throw response;
          });
      },

      /**
       * Update data on existing model instance
       *
       * @param {Object} item - Item to update
       * @param {Object} data - JSON data
       * @return {Promise<Object>} Updated model instance
       */
      update: function update(item, data) {
        var href;

        var found = _.find(service.models[item.model], function (obj) {
          return obj[item.primaryKey] === item[item.primaryKey];
        });

        if (!found) {
          found = item;
        }

        href = _.find(found.crud, { id: 'put' }).href;

        data.schema = found.schema;

        return ApiService
          .execute(href, 'PUT', data)
          .then(function (response) {
            _.forEach(service.models[item.model], function (entry) {
              if (entry[item.primaryKey] === item[item.primaryKey]) {
                _.forEach(_.keys(entry), function (key) {
                  entry[key] = response[key];
                });
              }
            });
            return response;
          })
          .catch(function (response) {
            ErrorService.requestError(response);
            throw response;
          });
      },

      /**
       * Delete an instance from a model
       * NOTE: "delete" is a reserved word.
       *
       * @param {Object} item - Item to remove
       * @return {Promise<Object>} Copy of deleted model instance
       */
      remove: function remove(item) {
        var href;

        var found = _.find(service.models[item.model], function (obj) {
          return obj[item.primaryKey] === item[item.primaryKey];
        });

        if (!found) {
          found = item;
        }

        href = _.find(found.crud, { id: 'delete' }).href;

        return ApiService
          .execute(href, 'DELETE', null)
          .then(function (response) {
            service.models[item.model] = _.reject(service.models[item.model], function (entry) {
              return entry[item.primaryKey] === item[item.primaryKey];
            });
            return response;
          })
          .catch(function (response) {
            ErrorService.requestError(response);
            throw response;
          });
      },

      /**
       * Obtains model from provided params and calls
       * CrudService to test crud.
       *
       * @param {string} crud - CRUD Identified to test
       * @param {string} model - Model key
       * @param {string} value - Value of property
       * @return {boolean} Crud exists
       */
      hasCrud: function hasCrud(crud, model, value) {
        var key = service.getPrimaryKey(model);

        return CrudService.hasCrud(
          crud,
          service.find(model, key, value)
        );
      },

      /**
       * Returns a string representing the primary key
       * of a given model
       *
       * @param {string} model - Model Identifier
       * @return {string} Primary key
       */
      getPrimaryKey: function getPrimaryKey(model) {
        switch (model) {
          case 'endpoints':
          case 'schemas':
            return 'label';
          default:
            return 'id';
        }
      }
    };

    return service;
  }
]);
